// Isomorphic: the server works out which currency and rates this page renders
// with; the browser reads back exactly what the server used (window.__MJ_FX,
// written by <FxScript> in app-root.tsx), so hydration starts from the same
// numbers and prices paint once. See packages/currency/src/ssr.ts for why.
//
// `createIsomorphicFn` keeps the server-only request readers out of the
// client bundle, as in session-hint.ts.
import { createIsomorphicFn } from "@tanstack/react-start";
import { getCookie, getRequestHeader } from "@tanstack/react-start/server";
import {
  CURRENCY_COOKIE,
  ratesFromBody,
  readFxGlobal,
  resolveRequestCurrency,
  type CurrencyCode,
  type FxSnapshot,
} from "@majourneys/currency";
import { apiBase } from "./api-base";

/**
 * Rates held by this worker isolate. The API refreshes them once a day and
 * edge-caches /currency/rates for a day, so ten minutes here costs nothing in
 * accuracy and keeps the fetch off almost every render.
 */
const RATES_TTL_MS = 10 * 60 * 1000;
/** A render never waits longer than this for rates; without them it falls back to the old client fetch. */
const RATES_TIMEOUT_MS = 800;

let cached: { at: number; rates: Record<CurrencyCode, number> } | null = null;
let inflight: Promise<Record<CurrencyCode, number> | null> | null = null;

async function serverRates(): Promise<Record<CurrencyCode, number> | null> {
  if (cached && Date.now() - cached.at < RATES_TTL_MS) return cached.rates;
  if (!inflight) {
    inflight = (async () => {
      try {
        const res = await fetch(`${apiBase()}/currency/rates`, {
          signal: AbortSignal.timeout(RATES_TIMEOUT_MS),
        });
        if (!res.ok) return cached?.rates ?? null;
        const rates = ratesFromBody(await res.json());
        if (rates) cached = { at: Date.now(), rates };
        return rates ?? cached?.rates ?? null;
      } catch {
        // A rate from earlier in this isolate beats none.
        return cached?.rates ?? null;
      } finally {
        inflight = null;
      }
    })();
  }
  return inflight;
}

export const readFxSnapshot = createIsomorphicFn()
  .client(async (): Promise<FxSnapshot | null> => readFxGlobal())
  .server(async (): Promise<FxSnapshot | null> => {
    try {
      // Same inputs, same rule as server.ts uses for the edge-cache key, so a
      // cached page is only ever served to a request that resolves the same.
      const { currency, explicit } = resolveRequestCurrency(
        getCookie(CURRENCY_COOKIE),
        readCountry(getRequestHeader("cf-ipcountry")),
      );
      return { currency, explicit, rates: await serverRates() };
    } catch {
      return null;
    }
  });

/** Mirrors lib/cf-country.ts for a bare header value. */
function readCountry(raw: string | undefined): string | null {
  const value = raw?.trim().toUpperCase();
  if (!value || value === "XX" || value === "T1") return null;
  return value;
}
